import { useDeliveryApi, useKernelApi } from 'infrastructure/api';
import { ResourceData, ResourceId, useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import {
  commercialStateValidation,
  completionBudgetHours,
  forecastCompletionDateValidation,
  progressValidation,
  projectUpdateFullInfoValidation,
  projectUpdatePartialInfoValidation,
  projectValidation,
} from './projectValidation';
import { ListResponse } from 'framework/list';
import projectInitialValidationShema from './createupdate/ProjectInitialValidationShema';
import { FormFieldsData } from 'framework/form';
import { isDocusignLinkValid, sumFtePositionsCount } from './utils';
import useProjectFinanceService from './finance/useProjectFinanceService';
import useProjectTeamService from './team/useProjectTeamService';
import { usePresaleOutputService } from 'domain/shared/presale';
import { ExpectedStructureType } from './team/types';
import { ProjectInitiationFormData } from './createupdate/types';
import projectUpdateInitValidationShema from './createupdate/ProjectUpdateInitValidationSchema';
import { COMMERCIAL_STATE, PROJECT_STATE } from './constants';
import { CURRENCY_TYPES } from '../core/constants';
import { AccountDto, ProjectSearchBaseDto, ProjectSearchDto, ProjectSuggestionsDto } from './types';
import { ContractRenewalStatusDto } from './controls/types';
import { CRM_PATH } from '../core/sharedRouteConstants';

export const NEW_PROJECT_PATH = '/newProjects';

type ExpectedStructureFormPosition = ExpectedStructureType & {
  isNewPosition?: boolean;
};

const useNewProjectService = () => {
  const singleResourceUrl = '/projects/:id';
  const {
    search: baseSearch,
    update: initialUpdate,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects',
    useService: useDeliveryApi,
    entityName: 'Project',
  });

  const { sendPostRequest, sendPutRequest, sendGetRequest, sendPatchRequest } = useDeliveryApi();
  const { sendPostRequest: sendPostKernelRequest } = useKernelApi();
  const { createBudget, updateBudgetInit, updateExistingBudget } = useProjectFinanceService();
  const { createProjectTeam, updateProjectTeam, getProjectTeamByProjectId } = useProjectTeamService();
  const { patchOpportunity } = usePresaleOutputService();

  const notificationsSearch = async (request: ResourceData) => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/notifications/search`, searchParams);
    return response.json();
  };
  const notificationsDelete = async (id: ResourceId) => {
    return await sendPatchRequest(`/notifications/${id}`);
  };

  const search = async (request: ResourceData): Promise<ListResponse<ProjectSearchDto>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/search`, searchParams);
    return response.json();
  };

  const searchSow = async (request: ResourceData): Promise<ListResponse<ProjectSearchDto>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/sow/search`, searchParams);
    return response.json();
  };

  const searchBase = async (request: ResourceData): Promise<ListResponse<ProjectSearchBaseDto>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/search-base`, searchParams);
    return response.json();
  };

  const searchSuggestions = async (request: ResourceData): Promise<ListResponse<ProjectSuggestionsDto>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/suggestions/search`, searchParams);
    return response.json();
  };

  const getValidationSchema = () => {
    return Promise.resolve(projectValidation);
  };

  const mapProject = ({ availableBudgets, rbsTreeNodeName, rbsTreeNodeId, ...project }: any) => {
    return {
      ...project,
      rbsTreeNode: {
        id: rbsTreeNodeId,
      },
      availableBudgetsIds: availableBudgets ? availableBudgets.map((budget: any) => budget.value) : [],
      sow: {
        id: project?.sow?.id,
      },
    };
  };
  const archiveProject = async (id: ResourceId) => {
    const response = await sendPatchRequest(`/projects/${id}/archive`);
    return response.json();
  };
  const unarchiveProject = async (id: ResourceId) => {
    const response = await sendPatchRequest(`/projects/${id}/unarchive`);
    return response.json();
  };

  const createProject = async (project: any) => {
    const response = await sendPostRequest('/projects', project);
    return response.json();
  };
  const activateProject = async (id: ResourceId, project: ResourceData) => {
    const response = await sendPostRequest(`/projects/${id}/activate`, project);
    return response.json();
  };

  const searchAccounts = async (request: ResourceData): Promise<ListResponse<AccountDto>> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/accounts/search`, searchParams);
    return response.json();
  };

  const getContractRenewalStatus = async (accountId: string, projectId?: number): Promise<ContractRenewalStatusDto> => {
    const response = await sendPostKernelRequest(`${CRM_PATH}/sale/accounts/${accountId}/renewal-status`, {
      projectId,
    });
    return response.json();
  };

  const uploadSOW = async (accountAlias: string, data: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/accounts/${accountAlias}/sow/upload`, data);
    return response.json();
  };

  const patchProjectInfo = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPatchRequest(`/projects/${id}`, data);
    return response.json();
  };
  const patchContractRenewal = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPatchRequest(`/projects/${id}/contract-renewal`, data);
    return response.json();
  };

  const patchProjectCommercialState = async (id: ResourceId) => {
    const response = await sendPatchRequest(`/projects/${id}/state-confirm`);
    return response.json();
  };

  const putProjectBudgetForecast = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/projects/${id}/forecast/budget`, data);
    return response.json();
  };

  const putProjectProgressForecast = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/projects/${id}/forecast/progress`, data);
    return response.json();
  };

  const putProjectTimelineForecast = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/projects/${id}/forecast/timeline`, data);
    return response.json();
  };

  const update = async (project: any) => {
    const response = await sendPutRequest('/projects', mapProject(project));
    return response.json();
  };

  const updateProject = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/projects/${id}`, data);
    return response.json();
  };

  const updateProjectPartial = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/projects/${id}/partial`, data);
    return response.json();
  };

  const updateState = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPatchRequest(`/projects/${id}/state-update`, data);
    return response.json();
  };

  const getName = async (id: ResourceId) => {
    const response = await sendPostRequest(`/projects/${id}/name/sensitive`);
    return response.json();
  };

  const getAccountRealName = async (id: ResourceId) => {
    const response = await sendGetRequest(`/projects/${id}/account-sensitive-name`);
    return response.json();
  };

  const archiveObjects = async (projectId: ResourceId, budgetId?: ResourceId) => {
    return sendPostRequest(`/archive/objects`, {
      projectId,
      budgetId,
    });
  };

  const getById = async (id: string) => {
    const response = await sendGetRequest(`/projects/${id}`);
    return response.json();
  };

  const getGlobalDeliveryPermissions = async (): Promise<string[]> => {
    try {
      const response = await sendGetRequest(`/permissions`);
      return await response.json();
    } catch (error) {
      return [];
    }
  };

  const getProjectEntityPermissions = async (entityName: string, entityId: ResourceId): Promise<string[]> => {
    try {
      if (!entityName || !entityId) {
        return [];
      }
      const response = await sendGetRequest(`/permissions/${entityName}/${entityId}`);
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `useNewProjectService: getPermissions: Failed to fetch permissions for ${entityName} ${entityId}:`,
        error,
      );
      return [];
    }
  };

  const initiateProject = async (formValues: FormFieldsData) => {
    const values = cloneDeep(formValues) as ProjectInitiationFormData;
    const rawProjectId = values.inputData.rawProjectId || values.opportunity.rawProjectId;

    const showExistingProject = !values.project.createNewProject;
    const shouldCreateDaVinciRedmine = values.project.createDaVinciRedmine;

    let project = null;
    const existingProjectId = values.project.existingProject?.id;
    if (shouldCreateDaVinciRedmine) {
      const projectData = showExistingProject ? await getById(existingProjectId) : null;

      project = rawProjectId
        ? await getById(String(rawProjectId))
        : await createProject({
            sowId: null,
            defaultBudgetId: null,
            accountName: values.opportunity.accountName,
            accountId: values.inputData.accountId,
            isAccountCodeName: projectData?.isAccountCodeName || null,
            assignmentForbidden: false,
            isArchived: false,
            name: projectData?.name || values.project.projectCodeName,
            originalName: projectData?.originalName || values.project.originalName,
            reportConfig: values.customerReport,
            requestId: values.requestId,
            engagementType: values.project.engagementType,
            commercialState: COMMERCIAL_STATE.COMMITTED,
          });
    } else if (!shouldCreateDaVinciRedmine) {
      project = await getById(existingProjectId);
    }

    const isDocusignValid = isDocusignLinkValid(values.sow?.docusignLink);
    const sow = values.sow?.docusignLink
      ? await uploadSOW(values.inputData.accountId, {
          accountAlias: values.inputData.accountId,
          docusignLink: isDocusignValid ? values.sow?.docusignLink : null,
          paths: !isDocusignValid ? values.sow?.relatedAttachments : null,
          sowNumber: Number(values.sow?.sowId),
          projectId: project.id,
        })
      : null;

    const createBudgetRequest = {
      sowId: sow?.sowNumber,
      projectName: project.name,
      billingType: values.budget.billingType,
      relatedDocuments: sow?.afsArtifactId ? [sow.afsArtifactId] : [],
      fte: sumFtePositionsCount(values?.team?.expectedStructure),
      isCommercial: true,
      hours: Number(values.budget.hours),
      amount: Number(values.budget.amount || 0),
      currency: CURRENCY_TYPES.USD,
      comment: 'Initial version of budget',
      softStartDate: values.budget.softStartDate,
      extendedEndDate: values.budget.extendedEndDate,
      startDate: values.budget.startDate,
      endDate: values.budget.endDate,
      requestId: values.requestId,
      isPrestart: !sow,
      expectedSowSigningDate: values.budget.expectedSowSigningDate,
    };

    const updateExistingBudgetData = {
      sowId: sow?.sowNumber,
      billingType: values.budget.billingType,
      relatedDocuments: sow?.afsArtifactId ? [sow.afsArtifactId] : [],
      fte: sumFtePositionsCount(values?.team?.expectedStructure),
      isCommercial: true,
      hours: Number(values.budget.hours),
      amount: Number(values.budget.amount),
      currency: CURRENCY_TYPES.USD,
      softStartDate: values.budget.softStartDate,
      extendedEndDate: values.budget.extendedEndDate,
      startDate: values.budget.startDate,
      endDate: values.budget.endDate,
      expectedSowSigningDate: values.budget.expectedSowSigningDate,
    };

    const existingBudgetId = values.project.existingProject?.defaultBudgetId || project.defaultBudgetId;
    const budget =
      !shouldCreateDaVinciRedmine && !rawProjectId
        ? await updateExistingBudget(existingBudgetId, updateExistingBudgetData)
        : await createBudget(createBudgetRequest);

    const shouldUseFormForecastSettings = shouldCreateDaVinciRedmine && values.project.createNewProject;

    const getForecastTrackingSettings = (forecastSettings?: {
      isProjectForecastTrackingEnabled?: boolean;
      isEvmTrackingEnabled?: boolean;
    }) => ({
      isProjectForecastTrackingEnabled: shouldUseFormForecastSettings
        ? (values.project.isProjectForecastTrackingEnabled ?? false)
        : (forecastSettings?.isProjectForecastTrackingEnabled ?? false),
      isEvmTrackingEnabled: shouldUseFormForecastSettings
        ? (values.project.isEvmTrackingEnabled ?? false)
        : (forecastSettings?.isEvmTrackingEnabled ?? false),
    });

    if (rawProjectId) {
      const projectData = showExistingProject && existingProjectId ? await getById(existingProjectId) : null;
      await Promise.all([
        activateProject(project.id, {
          projectId: project.id,
          name: projectData?.name || values.project.projectCodeName,
          originalName: projectData?.originalName || values.project.originalName,
          defaultBudgetId: budget.id,
          sowId: sow?.afsId,
          toolsInitState: PROJECT_STATE.IN_PROGRESS,
          engagementType: values.project.engagementType,
          commercialState: COMMERCIAL_STATE.COMMITTED,
          reportConfig: values.customerReport,
          scenario: values.project.scenario,
          ...getForecastTrackingSettings(project?.projectSettings?.forecastSettings),
        }),
      ]);
    } else {
      await Promise.all([
        patchOpportunity(values.inputData.opportunityId, {
          rawProjectId: Number(project.legacyId),
        }),
        patchProjectInfo(project.id, {
          requestId: values.requestId,
          defaultBudgetId: budget.id,
          sowId: sow?.afsId,
          toolsInitState: PROJECT_STATE.IN_PROGRESS,
          scenario: values.project.scenario,
          ...getForecastTrackingSettings(project?.projectSettings?.forecastSettings),
        }),
      ]);
    }

    const projectTeam = await getProjectTeamByProjectId(project.id);

    const team = {
      projectId: project.id,
      expectedStructure: values.team?.expectedStructure.map((position: ExpectedStructureFormPosition) => ({
        positionId: position.positionId,
        technologyId: position.technologyId,
        requiredRole: position.requiredRole,
        competencyLevel: position.competencyLevel,
        requiredFte: position.requiredFte,
        id: position.isNewPosition ? undefined : position.id,
      })),
    };

    if ('isNeedCreateTeam' in projectTeam) {
      await createProjectTeam(team);
    } else {
      await updateProjectTeam({ ...team, id: projectTeam.id });
    }

    return Promise.resolve(project.id);
  };

  const updateInitialProject = async (projectId: StringOrNumber, formValues: FormFieldsData) => {
    const values = cloneDeep(formValues) as ProjectInitiationFormData;

    const isDocusignValid = isDocusignLinkValid(values.sow.docusignLink);
    const sow = await uploadSOW(values.inputData.accountId, {
      accountAlias: values.inputData.accountId,
      docusignLink: isDocusignValid ? values.sow?.docusignLink : null,
      paths: !isDocusignValid ? values.sow?.relatedAttachments : null,
      sowNumber: values.sow?.sowId ? Number(values.sow?.sowId) : null,
      projectId: projectId,
    });

    const updateBudgetRequest = {
      sowId: sow?.sowNumber,
      projectName: formValues.project.existingProject.name,
      billingType: values.budget.billingType,
      relatedDocuments: sow?.afsArtifactId ? [sow.afsArtifactId] : [],
      fte: sumFtePositionsCount(values?.team?.expectedStructure),
      isCommercial: true,
      hours: Number(values.budget.hours),
      amount: Number(values.budget.amount),
      currency: CURRENCY_TYPES.USD,
      softStartDate: values.budget.softStartDate,
      extendedEndDate: values.budget.extendedEndDate,
      startDate: values.budget.startDate,
      endDate: values.budget.endDate,
      expectedSowSigningDate: values.budget.expectedSowSigningDate,
    };
    const budget = await updateBudgetInit(formValues.project.existingProject.defaultBudgetId, updateBudgetRequest);

    await Promise.all([
      patchProjectInfo(projectId, {
        requestId: values.requestId,
        defaultBudgetId: budget.id,
        sowId: sow?.afsId,
      }),
    ]);
    const team = {
      projectId: projectId,
      expectedStructure: values.team.expectedStructure.map((position: ExpectedStructureFormPosition) => ({
        positionId: position.positionId,
        technologyId: position.technologyId,
        requiredRole: position.requiredRole,
        competencyLevel: position.competencyLevel,
        requiredFte: position.requiredFte,
        id: position.isNewPosition ? undefined : position.id,
      })),
    };

    if (values.team.isNeedCreateTeam) {
      await createProjectTeam(team);
    } else {
      await updateProjectTeam({ ...team, id: values.team.id });
    }

    return Promise.resolve({
      id: projectId,
    });
  };

  const getProjectUpdateFullInfoValidationSchema = () => Promise.resolve(projectUpdateFullInfoValidation);
  const getProjectUpdatePartialInfoValidationSchema = () => Promise.resolve(projectUpdatePartialInfoValidation);
  const getProjectValidationSchema = () => Promise.resolve(projectValidation);
  const getProjectInitialValidationSchema = () => Promise.resolve(projectInitialValidationShema);
  const getProjectUpdateInitValidationSchema = () => Promise.resolve(projectUpdateInitValidationShema);
  const commercialStateValidationSchema = () => Promise.resolve(commercialStateValidation);
  const progressValidationSchema = () => Promise.resolve(progressValidation);
  const budgetCompletionValidationSchema = () => Promise.resolve(completionBudgetHours);
  const forecastCompletionDateValidationSchema = () => Promise.resolve(forecastCompletionDateValidation);

  const updateStaffingCoordinator = async (projectId: ResourceId, staffingCoordinator: string | null) => {
    const response = await sendPatchRequest(`/projects/${projectId}/staffing-coordinator`, {
      staffingCoordinator,
    });
    return response.json();
  };

  return {
    getGlobalDeliveryPermissions,
    getValidationSchema,
    createProject,
    getContractRenewalStatus,
    updateInitialProject,
    uploadSOW,
    patchProjectInfo,
    patchContractRenewal,
    getProjectEntityPermissions,
    ...baseCrudRequests,
    search,
    searchSow,
    searchAccounts,
    searchBase,
    getAccountRealName,
    searchSuggestions,
    getById,
    update,
    updateProject,
    updateState,
    initiateProject,
    updateProjectPartial,
    getName,
    archiveObjects,
    getProjectUpdateFullInfoValidationSchema,
    getProjectValidationSchema,
    getProjectInitialValidationSchema,
    getProjectUpdateInitValidationSchema,
    getProjectUpdatePartialInfoValidationSchema,
    commercialStateValidationSchema,
    progressValidationSchema,
    budgetCompletionValidationSchema,
    forecastCompletionDateValidationSchema,
    putProjectBudgetForecast,
    putProjectProgressForecast,
    putProjectTimelineForecast,
    patchProjectCommercialState,
    notificationsSearch,
    notificationsDelete,
    archiveProject,
    unarchiveProject,
    updateStaffingCoordinator,
  };
};

export default useNewProjectService;
